if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          
          // Make all thumbnail buttons focusable
          button.setAttribute('tabindex', '0');
        });
      
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        // Add keyboard navigation for thumbnails
        this.setupThumbnailKeyboardNavigation();
        
        // Initialize first thumbnail focus when connected
        this.initializeThumbnailFocus();
        
        // Keep existing slider navigation
        document.addEventListener('keydown', (event) => {
          if (!document.activeElement.classList.contains('thumbnail')) {
            if (event.key === 'ArrowLeft') {
              document.querySelector('.slider-button--prev')?.click();
            } else if (event.key === 'ArrowRight') {
              document.querySelector('.slider-button--next')?.click();
            }
          }
        });
      }
      
      // Move from connectedCallback to a separate method that's called in the constructor
      initializeThumbnailFocus() {
        if (!this.elements.thumbnails) return;
        
        // Find the active thumbnail or default to the first one
        const activeButton = this.elements.thumbnails.querySelector('[aria-current="true"] button');
        if (activeButton) {
          activeButton.setAttribute('tabindex', '0');
          // Focus the active thumbnail on page load
          setTimeout(() => activeButton.focus(), 100);
        } else {
          // If no active thumbnail, make first one focusable
          const firstButton = this.elements.thumbnails.querySelector('.thumbnail-list__item:first-child button');
          if (firstButton) {
            firstButton.setAttribute('tabindex', '0');
            // Focus the first thumbnail on page load
            setTimeout(() => firstButton.focus(), 100);
          }
        }
      }

      // New method to handle thumbnail keyboard navigation
      setupThumbnailKeyboardNavigation() {
        if (!this.elements.thumbnails) return;
        
        // Use event delegation at the thumbnail list level
        this.elements.thumbnails.addEventListener('keydown', (event) => {
          if (event.target.classList.contains('thumbnail')) {
            const thumbnailItem = event.target.closest('.thumbnail-list__item');
            
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              const prevItem = thumbnailItem.previousElementSibling;
              if (prevItem) {
                const prevButton = prevItem.querySelector('button');
                prevButton.focus();
                prevButton.click();
              }
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              const nextItem = thumbnailItem.nextElementSibling;
              if (nextItem) {
                const nextButton = nextItem.querySelector('button');
                nextButton.focus();
                nextButton.click();
              }
            }
          }
        });
        
        // Initialize first thumbnail when component is connected to DOM
        this.connectedCallback = function() {
          // Make the active thumbnail programmatically focusable
          // but don't auto-focus to avoid disrupting user's browsing
          const activeButton = this.elements.thumbnails.querySelector('[aria-current="true"] button');
          if (activeButton) {
            activeButton.setAttribute('tabindex', '0');
          } else {
            // If no active thumbnail, make first one focusable
            const firstButton = this.elements.thumbnails.querySelector('.thumbnail-list__item:first-child button');
            if (firstButton) {
              firstButton.setAttribute('tabindex', '0');
            }
          }
        };
      }


      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
